import React, { useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import type { DashboardData, ProviderAnalyticsData } from '@/api/queriesService';
import type { ReportData } from './generatePdfReport';
import { ALL_LLM_PROVIDERS } from '@/types/llm-provider';
import { Period, PERIOD_LABELS } from '@/utils/period';

interface GenerateReportButtonProps {
  dashboardData: DashboardData | undefined;
  providerAnalytics: ProviderAnalyticsData | undefined;
  websiteDomain: string;
  period: Period;
  periodStart: string;
  periodEnd: string;
}

const GenerateReportButton: React.FC<GenerateReportButtonProps> = ({
  dashboardData,
  providerAnalytics,
  websiteDomain,
  period,
  periodStart,
  periodEnd,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!dashboardData?.metrics || isGenerating) return;

    setIsGenerating(true);
    try {
      const { generatePdfReport } = await import('./generatePdfReport');

      const reportData: ReportData = {
        websiteDomain,
        periodLabel: PERIOD_LABELS[period],
        periodStart,
        periodEnd,
        metrics: {
          queriesTested: dashboardData.metrics.queriesTested,
          visibilityScore: dashboardData.metrics.visibilityScore,
          visibilityTrend: dashboardData.metrics.visibilityTrend,
          mentionPercentage: dashboardData.metrics.mentionPercentage,
          mentionTrend: dashboardData.metrics.mentionTrend,
        },
        providerSummaries: providerAnalytics?.providerSummaries ?? [],
        recentQueries: (dashboardData.recentQueries ?? []).slice(0, 10).map((q) => {
          const mentionCount = q.llmResults.filter((r) => r.mentioned).length;
          return {
            query: q.query,
            mentionCount,
            totalLlms: ALL_LLM_PROVIDERS.length,
          };
        }),
      };

      const doc = await generatePdfReport(reportData);

      const safeDomain = websiteDomain.replace(/[^a-zA-Z0-9.-]/g, '_');
      const safePeriod = period.replace(/\s+/g, '-');
      doc.save(`byllms-report-${safeDomain}-${safePeriod}.pdf`);
    } catch (err) {
      console.error('Failed to generate PDF report:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={handleGenerate}
      disabled={isGenerating || !dashboardData?.metrics}
      className="bg-[#3c00ff] text-white flex gap-2 h-12 items-center justify-center px-4 py-2.5 rounded hover:opacity-90 transition-opacity disabled:opacity-50"
    >
      {isGenerating ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <FileDown className="h-4 w-4" />
      )}
      <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase">
        {isGenerating ? 'Generating...' : 'Generate Report'}
      </span>
    </button>
  );
};

export default GenerateReportButton;
